import { useEffect, useMemo, useState } from 'react';
import {
  MAX_DECK_NICKNAME,
  NEGLECT_NIGHTS,
  PAIR_JOIN,
  PLACEMENT_GAMES,
  POWER_LEVELS,
  roman,
  deckEloSeries,
  deckLabel,
  deckRecord,
  deckRecordByPilot,
  display,
  formatRecord,
  games,
  isRanked,
  matchesForDeck,
  nightsSinceLastPlayed,
  streakForDeck,
  winRate,
} from '@edh/shared';
import type { Color, CommanderPick, Deck, ScryfallCard } from '@edh/shared';
import { canPair, sortColors } from '@edh/shared';
import { CommanderSearch } from '../components/CommanderSearch';
import { EloChart } from '../components/EloChart';
import { RatingBar, ratingRange } from '../components/RatingBar';
import { Sheet } from '../components/Sheet';
import { ColorPips } from './Decks';
import { fetchArt, scryfallCardById } from '../lib/api';
import { writeArt } from '../lib/db';
import { useDeckArt, usePartnerArt } from '../lib/art';
import { useLookups, useStore } from '../lib/store';

/**
 * The card itself, fetched once per commander and remembered.
 *
 * The deck row stores a Scryfall id and four fields off the card; everything
 * else — the rules text, whether it may take a partner, the full image — was
 * being thrown away at registration. Fetching on demand rather than storing it
 * keeps the league document small and means an errata'd card is right the next
 * time anybody looks, which is the correct trade for something read a few times
 * a season.
 *
 * The Lambda proxy already caches these for a day, so this costs one request
 * the first time a deck is opened and nothing after.
 */
const cards = new Map<string, ScryfallCard>();

function useCommanderCard(scryfallId: string | null): ScryfallCard | null {
  const [card, setCard] = useState<ScryfallCard | null>(() =>
    scryfallId ? (cards.get(scryfallId) ?? null) : null,
  );

  useEffect(() => {
    if (!scryfallId) return setCard(null);
    const known = cards.get(scryfallId);
    if (known) return setCard(known);
    let live = true;
    void scryfallCardById(scryfallId)
      .then((c) => {
        cards.set(scryfallId, c);
        // Fetch the scan now rather than when somebody taps the info button.
        // It arrives while they are reading the ratings, so the sheet usually
        // opens with the card already in the browser's cache.
        if (c.image) new Image().src = c.image;
        if (live) setCard(c);
      })
      .catch(() => {
        // Offline, or the id is gone. The page works without it; the controls
        // that need it simply do not appear.
      });
    return () => {
      live = false;
    };
  }, [scryfallId]);

  return card;
}

/**
 * The whole card, for reading what the commander actually does.
 *
 * A rules box people can reach without leaving the app and losing whatever
 * they were doing — the question "what does this thing even do" comes up when
 * somebody is looking at a deck they have never played, which is exactly this
 * screen.
 *
 * Image first, with the text under it. The image is the thing people recognise
 * and the text is what survives it failing to load, which on a phone in a shop
 * basement is not a rare case.
 */
/**
 * The card image, in a hole exactly its own shape.
 *
 * A Magic card is 63 by 88 millimetres and every scan Scryfall serves has that
 * ratio, so the space it will occupy is known before a single byte of it
 * arrives. Reserving it is the whole trick: without it the sheet opens at the
 * height of the text alone and then jumps when the image lands, which reads as
 * the app having got something wrong.
 *
 * A shimmer rather than a spinner. This is a placeholder for a thing whose size
 * is already decided, and the honest signal for that is the shape of the thing
 * itself, quietly busy.
 */
function CardImage({ card }: { card: ScryfallCard }) {
  const [state, setState] = useState<'loading' | 'shown' | 'failed'>('loading');

  if (!card.image || state === 'failed') return null;

  return (
    <div className={`card-shell${state === 'loading' ? ' loading' : ''}`}>
      <img
        src={card.image}
        alt={card.name}
        // Decoded off the main thread, so a large scan cannot hitch the sheet's
        // entry animation on the way in.
        decoding="async"
        onLoad={() => setState('shown')}
        onError={() => setState('failed')}
      />
    </div>
  );
}

function CardFace({ card }: { card: ScryfallCard }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <CardImage card={card} />
      <div className="panel">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
          <span style={{ fontSize: 13 }}>{card.type_line}</span>
          {card.mana_cost && <span className="num">{card.mana_cost}</span>}
        </div>
        {card.oracle_text && (
          // Newlines are load-bearing in rules text — abilities are one per
          // line, and a wall of run-together sentences is unreadable.
          <p style={{ whiteSpace: 'pre-wrap', margin: '10px 0 0', fontSize: 13, lineHeight: 1.5 }}>
            {card.oracle_text}
          </p>
        )}
      </div>
    </div>
  );
}

function CardSheet({
  cards,
  onClose,
}: {
  /** One, or both halves of a pair. */
  cards: ScryfallCard[];
  onClose: () => void;
}) {
  return (
    <Sheet title={cards.map((c) => c.name).join(PAIR_JOIN)} onClose={onClose}>
      {/* Both, for a pair. A partner deck is one deck with two rules boxes and
          showing only the first would answer half the question — which half
          depending on which commander happened to be registered first. */}
      {cards.map((card) => (
        <CardFace key={card.id} card={card} />
      ))}
    </Sheet>
  );
}

/**
 * The second commander, added or removed after the fact.
 *
 * Registration has always asked about partners and nothing else ever did, so a
 * deck registered as a single commander was one forever — which is the wrong
 * way round, because you find out a pair matters when somebody looks at the
 * page and cannot tell. Backgrounds and friends-forever get swapped far more
 * often than a deck gets rebuilt from scratch.
 *
 * The colour identity is recomputed from both, not merged into what is stored.
 * A pair shares a card pool, so the deck's colours are the union of the two —
 * and dropping the partner has to take its colours away again, which only works
 * if the primary commander's own identity is the thing being added to.
 */
function PartnerField({ deck, pairable }: { deck: Deck; pairable: boolean }) {
  const { updateDeck } = useStore();
  const [adding, setAdding] = useState(false);
  const [pick, setPick] = useState<CommanderPick | null>(null);
  const [manual, setManual] = useState('');
  const [busy, setBusy] = useState(false);

  const name = pick?.name ?? manual.trim();

  async function save() {
    if (!name || busy) return;
    setBusy(true);
    try {
      const colors: Color[] = sortColors([
        ...new Set([...(deck.soloColors ?? deck.colors), ...(pick?.colors ?? [])]),
      ]);
      await updateDeck(deck.id, {
        colors,
        // Kept so removing the partner can put the colours back. Without it the
        // union is one-way and a deck that briefly had a partner stays four
        // colours forever.
        soloColors: deck.soloColors ?? deck.colors,
        partner: {
          name,
          scryfallId: pick?.scryfallId ?? null,
          artCropUrl: pick?.artCropUrl ?? null,
        },
      });
      // The banner draws both faces, so both sets of bytes have to be local.
      try {
        if (pick?.artCropUrl) {
          await writeArt(`${deck.id}:partner`, await fetchArt(pick.artCropUrl));
        }
      } catch {
        // No signal; warmArtCache picks it up on the next sync.
      }
      setAdding(false);
      setPick(null);
      setManual('');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (busy) return;
    setBusy(true);
    try {
      await updateDeck(deck.id, {
        partner: null,
        colors: deck.soloColors ?? deck.colors,
      });
    } finally {
      setBusy(false);
    }
  }

  if (deck.partner) {
    return (
      <div className="field" style={{ marginBottom: 14 }}>
        <span>Partner</span>
        <div className="list-item">
          <span style={{ minWidth: 0 }}>{deck.partner.name}</span>
          <button className="invite-action" disabled={busy} onClick={() => void remove()}>
            Remove
          </button>
        </div>
      </div>
    );
  }

  /*
   * Only offered to a commander that can actually have one.
   *
   * Alela cannot partner with anything, so the button was an invitation to
   * record something illegal. Scryfall knows — the pairing mechanics are all
   * written on the card — so the answer comes from there rather than from a
   * list of names that would go stale the first time Wizards printed a new one.
   *
   * Hidden while the card is unknown, which covers being offline and covers a
   * deck typed in by hand with no Scryfall id at all. The cost of guessing
   * wrong in that direction is a button that shows up a moment later; guessing
   * the other way puts an illegal commander on somebody's deck.
   */
  if (!pairable) return null;

  if (!adding) {
    return (
      <button
        className="btn ghost block"
        style={{ marginBottom: 14 }}
        onClick={() => setAdding(true)}
      >
        Add a partner commander
      </button>
    );
  }

  return (
    <div style={{ marginBottom: 14 }}>
      <CommanderSearch
        label="Partner"
        placeholder="Malcolm, Keen-Eyed Navigator"
        onPick={setPick}
        onManual={(n) => {
          setManual(n);
          setPick(null);
        }}
      />
      <div className="btn-row">
        <button className="btn ghost" onClick={() => setAdding(false)}>
          Cancel
        </button>
        <button className="btn primary" disabled={!name || busy} onClick={() => void save()}>
          {busy ? <span className="spinner" /> : 'Add partner'}
        </button>
      </div>
    </div>
  );
}

/**
 * What the table calls this deck.
 *
 * Editable, because a nickname is the one field on a deck that is nobody's
 * business but the group's — it is not a fact about the card, it is the name
 * three people settled on, and those change. Until now it could only be set at
 * registration, in the ten seconds before anybody had played the thing and
 * therefore before it had a name worth having.
 *
 * Blank clears it, which puts the deck back to being called after its
 * commander. That is a real thing to want and not a validation error.
 */
function NicknameField({ deck }: { deck: Deck }) {
  const { updateDeck } = useStore();
  const stored = deck.nickname ?? '';
  const [value, setValue] = useState(stored);
  const [busy, setBusy] = useState(false);

  /*
   * Follow the stored value when it changes underneath — a sync landing, or
   * another phone renaming the same deck — but only while nothing is half
   * typed. Clobbering somebody mid-word to show them what they already agreed
   * to is the worse of the two failures.
   */
  useEffect(() => {
    setValue((current) => (current === '' || current === stored ? stored : current));
  }, [stored]);

  const dirty = value.trim() !== stored;

  async function save() {
    if (busy || !dirty) return;
    setBusy(true);
    try {
      await updateDeck(deck.id, { nickname: value.trim() || null });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginBottom: 10 }}>
      <label className="field" style={{ marginBottom: dirty ? 8 : 0 }}>
        {/* "Table name" rather than "nickname": it is not a pet name for the
            deck, it is what the group calls it out loud, which is the thing
            being asked for. */}
        <span>Table name</span>
        <input
          className="input"
          maxLength={MAX_DECK_NICKNAME}
          // The commander, so an empty box shows what the deck falls back to
          // being called rather than an instruction.
          placeholder={deck.name}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void save();
            if (e.key === 'Escape') setValue(stored);
          }}
        />
      </label>
      {/* Only once there is something to save. A button that is disabled more
          often than not is furniture, and this one has nothing to say while the
          box still holds what is already stored. */}
      {dirty && (
        <button className="btn primary block" disabled={busy} onClick={() => void save()}>
          {busy ? <span className="spinner" /> : value.trim() ? 'Save table name' : 'Clear table name'}
        </button>
      )}
    </div>
  );
}

/**
 * A deck, as a bottom drawer over whatever you were looking at.
 *
 * It stays route-driven even though it is not a page: the hash is what makes
 * the back gesture close it, which on a phone is the way people expect to get
 * out of something they slid up.
 */
export function DeckDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const { state, updateDeck, deleteDeck } = useStore();
  const { pilotName, deckName } = useLookups();
  const deck = state.decks.find((d) => d.id === id);
  const art = useDeckArt(deck);
  const partnerArt = usePartnerArt(deck);
  const [confirmRetire, setConfirmRetire] = useState(false);
  const [showCard, setShowCard] = useState(false);

  const played = useMemo(() => (deck ? matchesForDeck(state.matches, deck.id) : []), [state.matches, deck]);
  const byPilot = useMemo(() => (deck ? deckRecordByPilot(state.matches, deck.id) : {}), [state.matches, deck]);

  if (!deck) {
    return (
      <Sheet label="Deck" onClose={onClose}>
        <div className="empty">
          <strong>That deck is gone</strong>
          <button className="btn" style={{ marginTop: 14 }} onClick={onClose}>
            Back to the pool
          </button>
        </div>
      </Sheet>
    );
  }

  const record = deckRecord(state.matches, deck.id);
  const ranked = isRanked(deck.placementGames);
  const streak = streakForDeck(state.matches, deck.id);
  const idle = nightsSinceLastPlayed(state.matches, deck.id);
  const series = deckEloSeries(state.matches, deck.id, deck.elo);
  const range = ratingRange(
    state.decks.filter((d) => !d.retired && isRanked(d.placementGames)).map((d) => d.elo),
  );
  const rate = winRate(record);
  const card = useCommanderCard(deck.scryfallId);
  const partnerCard = useCommanderCard(deck.partner?.scryfallId ?? null);
  /*
   * Whatever we have, in the order they sit on the banner.
   *
   * A partner registered by hand has a name and no id, so its card may never
   * arrive — the sheet shows the one it has rather than refusing to open.
   */
  const faces = [card, partnerCard].filter((c): c is ScryfallCard => c !== null);

  return (
    <Sheet label={deckLabel(deck)} onClose={onClose}>
      {/* Two commanders share the banner, split down a diagonal — which is what
          a pair is: one deck, two faces, neither of them the main one. */}
      <div
        className={`detail-hero${deck.partner ? ' paired' : ''}`}
        style={!deck.partner && art ? { backgroundImage: `url(${art})` } : undefined}
      >
        {deck.partner && (
          <>
            <span
              className="hero-half left"
              style={art ? { backgroundImage: `url(${art})` } : undefined}
            />
            <span
              className="hero-half right"
              style={partnerArt ? { backgroundImage: `url(${partnerArt})` } : undefined}
            />
          </>
        )}
        {/* On the art rather than a row of its own below it. Reading the card
            is a thing you do to the commander, so the control belongs on the
            commander — and a full-width button for something consulted twice a
            season was the loudest thing on the page. */}
        {faces.length > 0 && (
          <button
            className="hero-info"
            onClick={() => setShowCard(true)}
            aria-label={`Read ${faces.length > 1 ? 'the commanders' : 'the card'}`}
          >
            i
          </button>
        )}
        <div className="detail-title">
          <h2>{deckLabel(deck)}</h2>
          {deck.nickname && (
            <p className="hint" style={{ margin: '2px 0 0' }}>
              {deck.name}
              {deck.partner ? `${PAIR_JOIN}${deck.partner.name}` : ''}
            </p>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '12px 2px' }}>
        <ColorPips colors={deck.colors} />
        {deck.ownerId && <span className="hint">built by {pilotName(deck.ownerId)}</span>}
        {deck.retired && <span className="pip">retired</span>}
      </div>

      {showCard && faces.length > 0 && (
        <CardSheet cards={faces} onClose={() => setShowCard(false)} />
      )}

      <NicknameField deck={deck} />
      {/*
        Gated on the card, with one deliberate exception: a deck typed in by
        hand has no Scryfall id, so there is nothing to ask and nothing to
        gate on. Blocking those would leave them unable to record a pair
        anywhere, since registration gates on the same answer.
      */}
      <PartnerField deck={deck} pairable={card ? canPair(card) : !deck.scryfallId} />

      {/* Editable, because a rebuild is exactly when a deck changes bracket and
          the button for recording that is a few lines below this one. */}
      <div className="field" style={{ marginBottom: 14 }}>
        <span>Power level</span>
        <div className="chips">
          {POWER_LEVELS.map((level) => (
            <button
              key={level}
              className="chip num"
              aria-pressed={deck.powerLevel === level}
              onClick={() =>
                void updateDeck(deck.id, {
                  powerLevel: deck.powerLevel === level ? null : level,
                })
              }
            >
              {roman(level)}
            </button>
          ))}
        </div>
      </div>

      {ranked ? (
        <RatingBar elo={deck.elo} min={range.min} max={range.max} colors={deck.colors} />
      ) : (
        // There is room for words here, so it says the whole thing rather than
        // the compact progress the tighter layouts use.
        <div className="panel" style={{ textAlign: 'center' }}>
          <div className="seat-label">Placement</div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, marginTop: 4 }}>
            {deck.placementGames} of {PLACEMENT_GAMES} games played
          </div>
          {/* The seeded starting rating used to be printed here. It is a number
              nobody can act on, and it is about to stop being true anyway —
              which is the whole point of playing the placement games. */}
        </div>
      )}

      <div className="stat-grid">
        <div className="stat">
          <div className="k">Rating</div>
          <div className="v">{ranked ? display(deck.elo) : '—'}</div>
        </div>
        <div className="stat">
          <div className="k">Peak</div>
          {/* A high-water mark is still the rating. It was the one number on
              this screen that went on answering "how is it doing" while the
              rating beside it was refusing to. */}
          <div className="v">
            {ranked && deck.peakElo > deck.elo + 1 ? display(deck.peakElo) : '—'}
          </div>
        </div>
        <div className="stat">
          <div className="k">Record</div>
          <div className="v">{formatRecord(record)}</div>
        </div>
        <div className="stat">
          <div className="k">Win rate</div>
          <div className="v">{rate === null ? '—' : `${Math.round(rate * 100)}%`}</div>
        </div>
      </div>

      {(streak && streak.length >= 3) || (idle !== null && idle >= NEGLECT_NIGHTS) ? (
        <p className="hint" style={{ margin: '0 2px 14px' }}>
          {streak && streak.length >= 3 &&
            `On a ${streak.length}-game ${streak.kind === 'win' ? 'run' : 'skid'}. `}
          {/* Spec 5, tier 3 — surfaces neglected decks without an obligation
              mechanic. Nights rather than games: a deck skipped through a
              fourteen-game weekend has been skipped once. */}
          {idle !== null &&
            idle >= NEGLECT_NIGHTS &&
            `Not played in ${idle} guild night${idle === 1 ? '' : 's'}.`}
        </p>
      ) : null}

      {/* No rank bands: decks have brackets, and a second ladder over the top
          would be two answers to one question. Sealed until placement is over,
          like every other view of the number. */}
      {ranked && series.length > 1 && (
        <>
          <div className="section-head">
            <h2>Rating</h2>
            <span className="hint">
              {series.length - 1} game{series.length === 2 ? '' : 's'}
            </span>
          </div>
          <EloChart
            series={series}
            color="var(--gold)"
            label={`${deckLabel(deck)}'s rating over every game it has played`}
          />
        </>
      )}

      {/*
        Who has played it, and how it went for each of them.

        The heading said "Architect", which named the wrong person entirely —
        the architect is whoever built the deck, and that is already at the top
        beside the colours. This is the list of everybody who has sleeved it up,
        which is the more interesting question of the two: the same deck going
        3-0 for one guildmate and 0-3 for another is the single most useful
        sentence the app can produce.
      */}
      <div className="section-head">
        <h2>Top players</h2>
      </div>
      {Object.keys(byPilot).length === 0 ? (
        <p className="hint" style={{ margin: '0 2px' }}>Nobody has sleeved this up yet.</p>
      ) : (
        <div className="list">
          {Object.entries(byPilot)
            .sort((a, b) => games(b[1]) - games(a[1]))
            .map(([pilotId, rec]) => (
              <div className="list-item" key={pilotId}>
                <span>{pilotName(pilotId)}</span>
                <span className="num">{formatRecord(rec)}</span>
              </div>
            ))}
        </div>
      )}

      <div className="section-head">
        <h2>Games</h2>
        <span className="hint num">{played.length}</span>
      </div>
      {played.length === 0 ? (
        <p className="hint" style={{ margin: '0 2px' }}>No games yet.</p>
      ) : (
        <div className="list">
          {played.slice(0, 25).map((m) => {
            const mine = m.entries.find((e) => e.deckId === deck.id)!;
            const won = mine.pilotId === m.winnerPilotId;
            const delta = m.deltas.decks[deck.id] ?? 0;
            const opponents = m.entries
              .filter((e) => e.deckId !== deck.id)
              .map((e) => deckName(e.deckId))
              .join(', ');
            return (
              <div className="list-item" key={m.id}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14 }}>
                    <span style={{ color: won ? '#57A566' : 'var(--muted)', fontWeight: 600 }}>
                      {won ? 'W' : 'L'}
                    </span>{' '}
                    vs {opponents}
                  </div>
                  <div className="sub">
                    {pilotName(mine.pilotId)} <span className="dot">·</span>{' '}
                    {new Date(m.ts).toLocaleDateString()}
                    {m.note && ` · “${m.note}”`}
                  </div>
                </div>
                {/* Sealed during placement. A per-game swing is the rating
                    served one spoonful at a time — five of these add up to
                    exactly the number the placement screen is withholding. */}
                {ranked ? (
                  <span className={delta >= 0 ? 'delta up' : 'delta down'}>
                    {delta >= 0 ? '+' : ''}
                    {Math.round(delta)}
                  </span>
                ) : (
                  <span className="hint num">—</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="btn-row" style={{ marginTop: 20 }}>
        <button
          className="btn ghost"
          onClick={() => void updateDeck(deck.id, { lastRebuiltAt: Date.now() })}
        >
          Mark rebuilt
        </button>
        {/* No games on record means nothing to preserve — a mistyped deck can be
            removed outright rather than left cluttering the pool as "retired". */}
        {played.length === 0 && deck.placementGames === 0 ? (
          confirmRetire ? (
            <button
              className="btn danger"
              onClick={() => {
                void deleteDeck(deck.id);
                onClose();
              }}
            >
              Confirm remove
            </button>
          ) : (
            <button className="btn ghost" onClick={() => setConfirmRetire(true)}>
              Remove
            </button>
          )
        ) : confirmRetire ? (
          <button
            className="btn danger"
            onClick={() => {
              void updateDeck(deck.id, { retired: !deck.retired });
              setConfirmRetire(false);
            }}
          >
            {deck.retired ? 'Bring back' : 'Confirm retire'}
          </button>
        ) : (
          <button className="btn ghost" onClick={() => setConfirmRetire(true)}>
            {deck.retired ? 'Bring back' : 'Retire'}
          </button>
        )}
      </div>
      {deck.lastRebuiltAt && (
        <p className="hint" style={{ textAlign: 'center', marginTop: 10 }}>
          Last rebuilt {new Date(deck.lastRebuiltAt).toLocaleDateString()}
        </p>
      )}

      {/*
        Every time the table changed its mind about what this deck is.

        Worth showing rather than only recording, because the bracket is not a
        label — it handicaps this deck's pilot against lower-bracket opponents,
        so it is a term in how every game was scored. A rating that looks odd is
        much easier to read with "and we moved it up in August" beside it.
      */}
      {(deck.bracketHistory?.length ?? 0) > 0 && (
        <>
          <div className="section-head">
            <h2>Bracket</h2>
          </div>
          <div className="list">
            {[...deck.bracketHistory!].reverse().map((change) => (
              <div className="list-item" key={change.at}>
                <span>
                  {change.from ? roman(change.from) : 'unset'} → {' '}
                  <strong>{change.to ? roman(change.to) : 'unset'}</strong>
                </span>
                <span className="sub">{new Date(change.at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
          <p className="hint" style={{ margin: '6px 2px 0' }}>
            Games before a change were scored under the bracket the deck had at the time.
            The rating itself is never moved by a reclassification once a deck has played —
            it has to earn the difference.
          </p>
        </>
      )}
    </Sheet>
  );
}
